/* eslint-disable react/require-default-props */
/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { ReactEditor } from 'slate-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/store';
import { selectCurrentOrganizationId } from '~/organizations/selectors';
import { BufferEditor } from '~/editor/BufferEditor/types.plate';
import { queryLimits } from '~/ai/state/aiLimits/thunks';
import { selectGenerateContentLimits } from '~/ai/state/aiLimits/slice';
import {
  contentGenerationStarted,
  contentGenerationFinished,
  selectContentGenerationStatus,
} from '~/ai/state/contentGeneration/slice';
import { URL_LEARN_MORE, ACTIONS } from './constants';
import { Message } from './Message';
import { Button } from './Button';
import {
  hasMinWords,
  hasRightLength,
  getRequirementsMessage,
  getWordsLenghtMessage,
  getPromptAnnotation,
} from './helpers';

import * as Styled from './styles';

/**
 *
 */
const AIAssistant = ({
  editor,
  service,
}: {
  editor: BufferEditor;
  service?: string;
}) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [usedAction, setUsedAction] = React.useState<string | null>(null);
  const [validLength, setValidLength] = React.useState<boolean>(false);
  const [validWords, setValidWords] = React.useState<boolean>(false);
  const [hasGeneratedContent, setHasGeneratedContent] =
    React.useState<boolean>(false);

  const organizationId = useAppSelector((state) =>
    selectCurrentOrganizationId(state)
  );

  const { contentGenerationInProgress } = useAppSelector((state) =>
    selectContentGenerationStatus(state)
  );

  const { canGenerateContent, limitsLoaded } = useAppSelector((state) =>
    selectGenerateContentLimits(state)
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const onGenerateText = (action: string) => {
    dispatch(contentGenerationStarted());
    setUsedAction(action);
    setHasGeneratedContent(false);

    const annotation = service ? getPromptAnnotation(service) : null;

    editor
      .generateText?.({
        organizationId,
        action,
        annotation,
      })
      .then(() => {
        dispatch(contentGenerationFinished());
        dispatch(queryLimits());
      })
      .catch((errorMessage) => {
        dispatch(contentGenerationFinished(errorMessage.message));
      });

    setOpen(false);
  };

  const onToggleOpen = () => {
    setOpen((opened) => !opened);
  };

  const onTryAgain = () => {
    if (!usedAction) return;
    setOpen(false);
    onGenerateText(usedAction);
  };

  const checkPrompt = () => {
    const prompt = editor.prompt?.().prompt;
    const hasSelection = !!editor.highlighted;

    if (!prompt) {
      setMessage(getRequirementsMessage(hasSelection));
      setValidWords(false);
      setValidLength(false);
      return;
    }

    const validWordsResult = hasMinWords(prompt);
    const validLengthResult = hasRightLength(prompt);
    setValidWords(validWordsResult);
    setValidLength(validLengthResult);
  };

  React.useEffect(() => {
    dispatch(queryLimits());
  }, []);

  React.useEffect(() => {
    checkPrompt();
  }, [editor.highlighted]);

  React.useEffect(() => {
    checkPrompt();
    setHasGeneratedContent(editor.hasGeneratedContent as boolean);
  }, [editor.children]);

  React.useEffect(() => {
    const elements = document.getElementsByClassName('generated-content-value');
    if (elements?.length) {
      const suggestion = elements[0];
      (suggestion as HTMLElement).style['scroll-margin-top'] = '25px';
      suggestion.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hasGeneratedContent]);

  React.useEffect(() => {
    const hasSelection = !!editor.highlighted;

    if (!validWords) {
      setMessage(getRequirementsMessage(hasSelection));
    } else if (!validLength) {
      setMessage(getWordsLenghtMessage(hasSelection));
    } else {
      setMessage(null);
    }
  }, [validWords, validLength]);

  const validPrompt = validWords && validLength;
  const actionsDisabled = !validPrompt || contentGenerationInProgress;

  const buttonDisabled =
    (limitsLoaded && !canGenerateContent) || contentGenerationInProgress;

  const buttonLabel = contentGenerationInProgress
    ? `${ACTIONS[usedAction || 'generateText'].loadingLabel}...`
    : t('content.generation.aiAssistantLabel');

  const actionsList = ['rephraseText', 'summarizeText', 'expandText'];

  const showProTip = validWords && !editor.highlighted && !hasGeneratedContent;

  return (
    <DropdownMenu.Root open={open}>
      <Styled.DropdownMenuTrigger
        onClick={hasGeneratedContent ? onTryAgain : onToggleOpen}
        disabled={buttonDisabled}
      >
        <Button
          label={buttonLabel}
          loading={contentGenerationInProgress}
          disabled={buttonDisabled}
          tryAgain={hasGeneratedContent}
        />
      </Styled.DropdownMenuTrigger>

      <DropdownMenu.Portal>
        <Styled.DropdownMenuContent
          align="start"
          alignOffset={4}
          sideOffset={8}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            ReactEditor.focus(editor as ReactEditor);
          }}
          onInteractOutside={(event) => {
            event.stopPropagation();
            setOpen(false);
          }}
        >
          {message && <Message text={message} url={URL_LEARN_MORE} colored />}

          <Styled.ActionsGroup>
            <Styled.DropdownMenuItem disabled={actionsDisabled}>
              <Styled.Action
                onClick={() => onGenerateText(ACTIONS.generateText.id)}
                disabled={actionsDisabled}
                flip
              >
                {ACTIONS.generateText.icon}
                {ACTIONS.generateText.label}{' '}
                {editor.highlighted ? 'from selection' : ''}
              </Styled.Action>
            </Styled.DropdownMenuItem>
          </Styled.ActionsGroup>

          <Styled.ActionsGroup>
            {actionsList.map((action) => (
              <Styled.DropdownMenuItem
                key={ACTIONS[action].id}
                disabled={actionsDisabled}
              >
                <Styled.Action
                  onClick={() => onGenerateText(ACTIONS[action].id)}
                  disabled={actionsDisabled}
                >
                  {ACTIONS[action].icon}
                  {ACTIONS[action].label}{' '}
                  {editor.highlighted ? 'selection' : ''}
                </Styled.Action>
              </Styled.DropdownMenuItem>
            ))}
          </Styled.ActionsGroup>

          {showProTip && (
            <Message
              text="Pro Tip: Select parts of your copy to use as a prompt."
              url={URL_LEARN_MORE}
              colored={false}
            />
          )}
        </Styled.DropdownMenuContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export { AIAssistant };
